import { Add, Close } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid2,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useState } from 'react';
import { addFacilityPersonnel } from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';

export default function PersonnelAdd({ open, onClose, facilityId }) {
  const nigDialCode = '+234';
  const [canReceiveCert, setCanReceiveCert] = useState(false);
  const [canReceiveInvoice, setCanReceiveInvoice] = useState(false);
  const [makeAdmin, setMakeAdmin] = useState(false);
  const [saving, setSaving] = useState(false);

  const formik = useFormik({
    initialValues: {
      fullName: '',
      email: '',
      phone: '',
      designation: '',
      receiveInvoiceEmail: '',
      receiveCertEmail: '',
      facilityId: '',
      facilityName: '',
    },
    validationSchema: Yup.object({
      fullName: Yup.string().required('Full Name is required'),
      email: Yup.string().required('Email Address is required'),
      phone: Yup.string().required('Phone number is required'),
      designation: Yup.string().required('Designation number is required'),
    }),
    onSubmit: (values) => handleAddFacilityPersonels(values),
  });

  const handleAddFacilityPersonels = async (values) => {
    setSaving(true);
    try {
      let phoneNumber = values?.phone;
      if (!phoneNumber) {
        openNotification({ status: 'error', title: 'Phone number is required' });
      }

      if (phoneNumber?.charAt(0) === '0') {
        phoneNumber = phoneNumber?.slice(1);
      }

      const fullPhoneNumber = `${nigDialCode?.slice(1)}${phoneNumber}`;

      if (fullPhoneNumber?.length > 13 || phoneNumber < 13) {
        openNotification({ type: 'error', title: 'Phone number is possibly incorrect' });
      }

      const payload = {
        fullName: values?.fullName,
        email: values?.email,
        phone: fullPhoneNumber,
        designation: values?.designation,
        receiveInvoiceEmail: canReceiveInvoice,
        receiveCertEmail: canReceiveCert,
        isAdministrator: makeAdmin,
      };

      const res = await addFacilityPersonnel(facilityId, [payload]);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        openNotification({ type: 'success', message: status?.desc });
        onClose();
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      openNotification({ type: 'error', message: 'Something went wrong. Please try again' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <DialogTitle variant="body1" sx={{ fontWeight: 600 }}>
          Add Personnel
        </DialogTitle>
        <Box ml="auto" px={3}>
          <IconButton onClick={() => onClose()} disabled={saving}>
            <Close />
          </IconButton>
        </Box>
      </Box>
      <DialogContent sx={{}}>
        <form onSubmit={formik.handleSubmit}>
          <Grid2 container spacing={2}>
            <Grid2 size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
              <Typography variant="body2" color="textSecondary">
                Enter the information of the facility contact persons
              </Typography>
            </Grid2>
            <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
              <TextField
                name="fullName"
                label="Full Name"
                variant="outlined"
                value={formik.values.fullName}
                fullWidth
                onChange={formik.handleChange}
                helperText={formik.touched.fullName && formik.errors.fullName}
                error={formik.touched.fullName && Boolean(formik.errors.fullName)}
              />
            </Grid2>
            <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
              <TextField
                name="designation"
                label="Designation"
                variant="outlined"
                value={formik.values.designation}
                fullWidth
                onChange={formik.handleChange}
                helperText={formik.touched.designation && formik.errors.designation}
                error={formik.touched.designation && Boolean(formik.errors.designation)}
              />
            </Grid2>
            <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
              <TextField
                name="email"
                label="Email"
                variant="outlined"
                value={formik.values.email}
                fullWidth
                type="email"
                onChange={formik.handleChange}
                helperText={formik.touched.email && formik.errors.email}
                error={formik.touched.email && Boolean(formik.errors.email)}
              />
            </Grid2>
            <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
              <TextField
                name="phone"
                label="Phone No."
                variant="outlined"
                value={formik.values.phone}
                fullWidth
                onChange={formik.handleChange}
                //required
                slotProps={{
                  input: {
                    startAdornment: <Typography>{nigDialCode}</Typography>,
                  },
                }}
                helperText={formik.touched.phone && formik.errors.phone}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
              />
            </Grid2>

            <Grid2 size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
              <FormControlLabel
                label="Can receive the certificate in their email"
                control={
                  <Checkbox
                    checked={canReceiveCert}
                    onChange={(e) => setCanReceiveCert(e.target.checked)}
                  />
                }
              />

              <FormControlLabel
                label="Can receive the invoice in their email"
                control={
                  <Checkbox
                    checked={canReceiveInvoice}
                    onChange={(e) => setCanReceiveInvoice(e.target.checked)}
                  />
                }
              />
            </Grid2>

            <Grid2 size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
              <Alert severity="info" icon={false}>
                <FormControlLabel
                  control={
                    <Switch checked={makeAdmin} onChange={(e) => setMakeAdmin(e.target.checked)} />
                  }
                  label="Make Admin"
                />
                {makeAdmin && (
                  <Alert
                    severity="warning"
                    icon={false}
                  >{`An invite will be sent and the personnel will have access to required facility information`}</Alert>
                )}
              </Alert>
            </Grid2>

            <Grid2 size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'end', pb: 1 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="info"
                  disabled={saving}
                  startIcon={!saving ? <Add /> : <CircularProgress size={20} />}
                  sx={{ textTransform: 'none', borderRadius: 20 }}
                >
                  Add Personnel
                </Button>
              </Box>
            </Grid2>
          </Grid2>
        </form>
      </DialogContent>
    </Dialog>
  );
}
